import { useState } from "react";

import { setFlashRedirectToCurrentLocation } from "@impero-com/common/utils/flash";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { queryClient } from "@impero-com/common/utils/query_client";
import { Button, InformationModal, renderMarkdown } from "@impero-com/ui";

import { getPing } from "api/generated/authentication/authentication";
import { getGetUserStatusQueryKey } from "api/generated/utils/utils";

import { orgLogout } from "../utils/logout";
import { useUserStatusQuery } from "../utils/queries/useUserStatusQuery";

const SESSION_TIMEOUT_WARNING_THRESHOLD_SECONDS = 300;

export function OrgSessionTimeoutOverlay() {
    const { data: userStatus } = useUserStatusQuery();
    const { session: sessionStatus } = userStatus;
    switch (sessionStatus.type) {
        case "LoggedIn": {
            if (
                sessionStatus.sessionTimeout <=
                SESSION_TIMEOUT_WARNING_THRESHOLD_SECONDS
            ) {
                return (
                    <SessionTimeoutWarningPopup
                        isRenewable={sessionStatus.isRenewable}
                    />
                );
            } else {
                return null;
            }
        }
        case "PendingTwoFactor":
        case "Anonymous": {
            return (
                <InformationModal
                    open
                    // Nothing to go back to: logging in again is the only way out.
                    onClose={() => {}}
                    type="info"
                    icon="logOut"
                    title={t("Session_Timeout_Title")}
                    footer={
                        <Button
                            id="log-in"
                            variant="primary"
                            onClick={() => {
                                setFlashRedirectToCurrentLocation();
                                window.location.assign("/");
                            }}
                        >
                            {t("Session_Timeout_LoginAgain")}
                        </Button>
                    }
                >
                    {renderMarkdown(t("Session_Timeout_Body"))}
                </InformationModal>
            );
        }
        default: {
            return assertNever(sessionStatus);
        }
    }
}

interface SessionTimeoutWarningPopupProps {
    isRenewable: boolean;
}

function SessionTimeoutWarningPopup({
    isRenewable,
}: SessionTimeoutWarningPopupProps) {
    const [open, setOpen] = useState(true);

    return (
        <InformationModal
            open={open}
            // Only the action row closes this: a stray Esc shouldn't hide the warning.
            onClose={() => {}}
            type="warning"
            title={
                isRenewable
                    ? t("Session_Timeout_Warning_Title")
                    : t("Session_Timeout_Warning_NonRenewable_Title")
            }
            footer={
                <>
                    <Button id="log-out" onClick={orgLogout}>
                        {t("Session_Timeout_Warning_LogOut")}
                    </Button>
                    {isRenewable ? (
                        <Button
                            id="stay-logged-in"
                            variant="primary"
                            onClick={() => {
                                getPing().then(() => {
                                    queryClient.invalidateQueries({
                                        queryKey: getGetUserStatusQueryKey(),
                                    });
                                });
                            }}
                        >
                            {t("Session_Timeout_Warning_StayLoggedIn")}
                        </Button>
                    ) : (
                        <Button
                            id="close"
                            variant="primary"
                            onClick={() => setOpen(false)}
                        >
                            {t("Session_Timeout_Warning_Close")}
                        </Button>
                    )}
                </>
            }
        >
            {renderMarkdown(
                isRenewable
                    ? t("Session_Timeout_Warning_Body")
                    : t("Session_Timeout_Warning_NonRenewable_Body")
            )}
        </InformationModal>
    );
}
